import type {
  ProjectSettings,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import type { FC } from "react";
import { useNavigate } from "react-router";
import { useAllFormsQuery } from "../../../../api/entities/form.js";
import { appPaths } from "../../../../app/appPaths.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";

interface JobOpeningPreInterviewFormSettingProps {
  project: ProjectWithInterviewCount;
  onSaveProject: (project: {
    id: string;
    settings?: ProjectSettings;
  }) => Promise<void>;
}

export const JobOpeningPreInterviewFormSetting: FC<
  JobOpeningPreInterviewFormSettingProps
> = ({ project, onSaveProject }) => {
  const navigate = useNavigate();
  const { form_id } = project.settings;
  const { data: allForms, isLoading } = useAllFormsQuery();

  const formOptions: SimpleSelectOption<string | null>[] = [
    {
      value: null,
      label: "None",
    },
    ...(allForms?.map((form) => ({
      value: form.id,
      label: form.name,
    })) || []),
  ];

  const handleFormChange = async (formId: string | null) => {
    await onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        form_id: formId,
      },
    });
  };

  return (
    <div className="mt-6">
      <Can
        I={PermissionActions.UPDATE}
        a={PermissionSubjects.FORMS}
        passThrough={true}
      >
        {(allowed) => (
          <div className="flex flex-col gap-2">
            <Label size={Size.SMALL}>Pre-interview form</Label>
            {allForms && allForms.length > 0 ? (
              <>
                <div className="w-[200px]">
                  <SimpleSelect
                    label="Pre-interview form"
                    isLabelHidden
                    value={form_id || null}
                    onChange={handleFormChange}
                    options={formOptions}
                    isDisabled={!allowed || isLoading}
                    placeholder="Select a form"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  Collect information from participants before their interview.
                  Manage your pre-interview forms in{" "}
                  <span
                    className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                    onClick={() => {
                      navigate(appPaths.settingsForms.build());
                    }}
                  >
                    settings
                  </span>
                  .
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                Collect information from participants before their interview.
                Create forms in{" "}
                <span
                  className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                  onClick={() => {
                    navigate(appPaths.settingsForms.build());
                  }}
                >
                  settings
                </span>
                , then select one here.
              </div>
            )}
          </div>
        )}
      </Can>
    </div>
  );
};
